import { useMemo, useState } from 'react';
import { Button, Card, DataTable, InlineStack, SkeletonBodyText, Text } from '@shopify/polaris';
import { Area, Bar, CartesianGrid, Cell, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { Delta } from '../deep/Delta';
import { deltaPercent, formatInteger, formatShortDate, toNumber } from '../deep/analyticsHelpers';
import { AXIS_TICK, COLORS, formatAxisCurrency, formatEur } from './chartTheme';

/*
  One metric over the selected periods, with the comparable period a year earlier in gray.
    kind = 'bars'       flows per period (order intake, revenue, costs)
    kind = 'diverging'  flows that can go below zero (gross margin): red below the baseline
    kind = 'area'       a value at the end of each period (orderbook)
  `points` come from dashboardData: { key, label, title, partial, current: { v, n }, previous: { v, n } }.
*/

const PREVIOUS_LABEL = 'a year earlier';

function Swatch({ color, line = false }) {
  return line ? (
    <span style={{ display: 'inline-block', width: 14, height: 2, background: color, borderRadius: 1, verticalAlign: 'middle' }} />
  ) : (
    <span style={{ display: 'inline-block', width: 10, height: 10, background: color, borderRadius: 2, verticalAlign: 'middle' }} />
  );
}

function LegendRow({ accent, kind, hasPartial }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: '#6d7175', margin: '8px 0 2px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Swatch color={accent} line={kind === 'area'} /> {kind === 'area' ? 'End of period' : 'This period'}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Swatch color={COLORS.previous} line={kind === 'area'} /> {PREVIOUS_LABEL}
      </span>
      {kind === 'diverging' ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Swatch color={COLORS.marginNegative} /> negative
        </span>
      ) : null}
      {hasPartial && kind !== 'area' ? <span>lighter bar = running period so far</span> : null}
    </div>
  );
}

function PeriodTooltip({ active, payload, accent, kind, countLabel }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  const delta = deltaPercent(point.current, point.previous);
  const row = (color, label, value, strong) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6d7175' }}>
        <Swatch color={color} line={kind === 'area'} /> {label}
      </span>
      <span style={{ fontWeight: strong ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>{formatEur(value)}</span>
    </div>
  );
  return (
    <div style={{ background: '#fff', border: '1px solid #e3e3e3', borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12, minWidth: 220 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {kind === 'area' ? `On ${formatShortDate(point.at)}` : point.title}
        {point.partial && kind !== 'area' ? ' (so far)' : ''}
      </div>
      {row(point.current < 0 && kind === 'diverging' ? COLORS.marginNegative : accent, kind === 'area' ? 'value' : 'this period', point.current, true)}
      {row(COLORS.previous, PREVIOUS_LABEL, point.previous, false)}
      {delta !== null ? (
        <div style={{ marginTop: 4 }}>
          <Delta current={point.current} previous={point.previous} invert={point.invert} />
        </div>
      ) : null}
      {countLabel && point.n ? <div style={{ color: '#6d7175', marginTop: 4 }}>{`${formatInteger(point.n)} ${countLabel}`}</div> : null}
    </div>
  );
}

export default function PeriodChartCard({ title, subtitle, points, kind = 'bars', accent = COLORS.intake, invert = false, countLabel = null, loading = false }) {
  const [showTable, setShowTable] = useState(false);

  const data = useMemo(
    () =>
      (points || []).map((point) => ({
        key: point.key,
        label: point.label,
        title: point.title,
        partial: point.partial,
        at: point.at,
        current: Math.round(toNumber(point.current?.v)),
        previous: Math.round(toNumber(point.previous?.v)),
        n: toNumber(point.current?.n),
        invert
      })),
    [points, invert]
  );

  // headline: the total over the visible range (flows) or the latest value (point in time)
  const headline = useMemo(() => {
    if (!data.length) return null;
    if (kind === 'area') {
      const last = data[data.length - 1];
      return { label: `on ${formatShortDate(last.at)}`, current: last.current, previous: last.previous };
    }
    return {
      label: 'total shown',
      current: data.reduce((sum, point) => sum + point.current, 0),
      previous: data.reduce((sum, point) => sum + point.previous, 0)
    };
  }, [data, kind]);

  const hasPartial = data.some((point) => point.partial);
  const barProps = { radius: [4, 4, 0, 0], maxBarSize: 24, isAnimationActive: false };

  const tableRows = data.map((point) => [
    kind === 'area' ? `${point.title} (on ${formatShortDate(point.at)})` : `${point.title}${point.partial ? ' (so far)' : ''}`,
    formatEur(point.current),
    formatEur(point.previous),
    <Delta current={point.current} previous={point.previous} invert={invert} />,
    ...(countLabel ? [formatInteger(point.n)] : [])
  ]);

  return (
    <Card>
      <InlineStack align="space-between" blockAlign="start" wrap={false} gap="400">
        <div style={{ minWidth: 0 }}>
          <CardHeading title={title} />
          {subtitle ? (
            <Text as="p" variant="bodySm" tone="subdued">
              {subtitle}
            </Text>
          ) : null}
        </div>
        {headline ? (
          <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
            <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{formatEur(headline.current)}</div>
            <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              <Delta current={headline.current} previous={headline.previous} invert={invert} />{' '}
              <Text as="span" variant="bodySm" tone="subdued">
                {`${headline.label}, vs ${PREVIOUS_LABEL}`}
              </Text>
            </div>
          </div>
        ) : null}
      </InlineStack>
      {!points ? (
        <div style={{ marginTop: 16 }}>
          <SkeletonBodyText lines={6} />
        </div>
      ) : (
        <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 200ms' }}>
          <LegendRow accent={accent} kind={kind} hasPartial={hasPartial} />
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap="28%">
                <CartesianGrid vertical={false} stroke={COLORS.grid} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={18} interval="preserveStartEnd" />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={formatAxisCurrency} width={58} />
                <Tooltip
                  content={<PeriodTooltip accent={accent} kind={kind} countLabel={countLabel} />}
                  cursor={kind === 'area' ? { stroke: COLORS.axis, strokeWidth: 1 } : { fill: 'rgba(0, 0, 0, 0.04)' }}
                />
                {kind === 'diverging' ? <ReferenceLine y={0} stroke={COLORS.axis} /> : null}
                {kind === 'area' ? (
                  <Line
                    dataKey="previous"
                    name={PREVIOUS_LABEL}
                    stroke={COLORS.previous}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: COLORS.previous }}
                    isAnimationActive={false}
                  />
                ) : (
                  <Bar dataKey="previous" name={PREVIOUS_LABEL} fill={COLORS.previous} {...barProps} />
                )}
                {kind === 'area' ? (
                  <Area
                    dataKey="current"
                    name="End of period"
                    type="monotone"
                    stroke={accent}
                    strokeWidth={2}
                    fill={accent}
                    fillOpacity={0.1}
                    dot={false}
                    activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: accent }}
                    isAnimationActive={false}
                  />
                ) : (
                  <Bar dataKey="current" name="This period" fill={accent} {...barProps}>
                    {data.map((point) => (
                      <Cell key={point.key} fill={kind === 'diverging' && point.current < 0 ? COLORS.marginNegative : accent} fillOpacity={point.partial ? 0.5 : 1} />
                    ))}
                  </Bar>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <InlineStack align="end">
            <Button variant="plain" onClick={() => setShowTable((value) => !value)}>
              {showTable ? 'Hide table' : 'Show table'}
            </Button>
          </InlineStack>
          {showTable ? (
            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', ...(countLabel ? ['numeric'] : [])]}
              headings={[<b>Period</b>, <b>{kind === 'area' ? 'Value' : 'This period'}</b>, <b>A year earlier</b>, <b>Δ</b>, ...(countLabel ? [<b>{countLabel}</b>] : [])]}
              rows={tableRows}
              totals={
                kind === 'area' || !headline
                  ? undefined
                  : [
                      '',
                      formatEur(headline.current),
                      formatEur(headline.previous),
                      <Delta current={headline.current} previous={headline.previous} invert={invert} />,
                      ...(countLabel ? [formatInteger(data.reduce((sum, point) => sum + point.n, 0))] : [])
                    ]
              }
              totalsName={{ singular: 'Total', plural: 'Total' }}
              showTotalsInFooter
            />
          ) : null}
        </div>
      )}
      {points && !data.length ? (
        <Text as="p" tone="subdued" variant="bodySm">
          Nothing in this range.
        </Text>
      ) : null}
      {points && data.length > 0 && hasPartial && kind === 'area' ? (
        <Text as="p" tone="subdued" variant="bodySm">
          The last point is today; its comparison is the same day a year earlier.
        </Text>
      ) : null}
    </Card>
  );
}
